/**
 * 展示维度 icon 大型资源映射表。
 *
 * @core DimensionMapSetModal 写入与 DimNumberTrendCard / 多维展示读同一资源表；
 *       含 asset import，故放 config 而非 constants。
 */

import Steam from '@/pages/home/assets/svg/dataSource/theme-icon/steam-theme.svg';
import Xbox from '@/pages/home/assets/svg/dataSource/theme-icon/xbox-theme.svg';
import Playstation from '@/pages/home/assets/svg/dataSource/theme-icon/playstation-theme.svg';
import Epic from '@/pages/home/assets/svg/dataSource/theme-icon/epic-theme.svg';
import Nintendo from '@/pages/home/assets/svg/dataSource/theme-icon/nintendo-theme.svg';
import Default from '@/pages/home/assets/svg/dataSource/theme-icon/socialmedia-theme.svg';
import SteamDisable from '@/pages/home/assets/svg/dataSource/theme-icon/disableIcon/steam.svg';
import XboxDisable from '@/pages/home/assets/svg/dataSource/theme-icon/disableIcon/xbox.svg';
import PlaystationDisable from '@/pages/home/assets/svg/dataSource/theme-icon/disableIcon/playstation.svg';
import EpicDisable from '@/pages/home/assets/svg/dataSource/theme-icon/disableIcon/epic.svg';
import NintendoDisable from '@/pages/home/assets/svg/dataSource/theme-icon/disableIcon/nintendo.svg';
import DefaultDisable from '@/pages/home/assets/svg/dataSource/theme-icon/disableIcon/socialmedia.svg';
import MonitorDisable from '@/pages/home/assets/svg/dataSource/theme-icon/disableIcon/monitor.svg';
import IphoneDisable from '@/pages/home/assets/svg/dataSource/theme-icon/disableIcon/iphone.svg';
import ConsoleDisable from '@/pages/home/assets/svg/dataSource/theme-icon/disableIcon/console.svg';
import Monitor from '@/pages/home/assets/svg/dataSource/theme-icon/monitor-theme.svg';
import Iphone from '@/pages/home/assets/svg/dataSource/theme-icon/iphone-theme.svg';
import Console from '@/pages/home/assets/svg/dataSource/theme-icon/console-theme.svg';
import {
  COUNTRY_FLAG_ICON_CODES,
  getCountryFlag,
} from '@/pages/home/_config/countries';
import { SHOW_DIMENSION_DEFAULT_COLOR } from './constants';
import type { IDimensionTrendIconMeta, IDimensionTrendIconUrlEntry } from './types';

const createDimensionSvgDataUri = (pathList: string[], fill: string): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">${pathList
      .map(path => `<path d="${path}" fill="${fill}" ></path>`)
      .join('')}</svg>`,
  )}`;

const SENTIMENT_COMMON_PATHS = [
  'M307.2 396.8a64 64 0 1 1 64 64 64 64 0 0 1-64-64z',
  'M652.8 332.8a64 64 0 1 0 64 64 64 64 0 0 0-64-64z',
  'M512 0a512 512 0 1 0 512 512 512 512 0 0 0-512-512zM102.4 512A409.6 409.6 0 1 1 512 921.6 409.6 409.6 0 0 1 102.4 512z',
];

const SENTIMENT_POSITIVE_ICON = createDimensionSvgDataUri(
  [
    'M512 716.8a204.8 204.8 0 0 1-180.288-107.52l-90.048 48.704a307.2 307.2 0 0 0 540.736 0l-90.112-48.704A204.8 204.8 0 0 1 512 716.8z',
    ...SENTIMENT_COMMON_PATHS,
  ],
  '#0ABF5B',
);

const SENTIMENT_NEGATIVE_ICON = createDimensionSvgDataUri(
  [
    'M512 659.2a204.8 204.8 0 0 0-180.288 107.584l-90.048-48.704a307.2 307.2 0 0 1 540.736 0l-90.048 48.704A204.8 204.8 0 0 0 512 659.2z',
    ...SENTIMENT_COMMON_PATHS,
  ],
  '#E54545',
);

const SENTIMENT_NEUTRAL_ICON = createDimensionSvgDataUri(
  ['M716.8 716.8V614.4H307.2v102.4z', ...SENTIMENT_COMMON_PATHS],
  '#838697',
);

function buildDimensionCountryIconMap(): Record<
  string,
  IDimensionTrendIconUrlEntry
> {
  const cololist = SHOW_DIMENSION_DEFAULT_COLOR;
  return Object.fromEntries(
    COUNTRY_FLAG_ICON_CODES.map(
      (code): [string, IDimensionTrendIconUrlEntry] => [
        code,
        {
          icon: getCountryFlag(code),
          disableIcon: getCountryFlag(code),
          cololist,
        },
      ],
    ),
  ) as Record<string, IDimensionTrendIconUrlEntry>;
}

/** icon key → 资源元数据（含国旗、平台、情感等）。 */
export const SHOW_DIMENSION_ICON_MAP = {
  ...buildDimensionCountryIconMap(),
  xbox: { icon: Xbox, disableIcon: XboxDisable, color: '#107C10' },
  epics: { icon: Epic, disableIcon: EpicDisable, color: '#F8C800' },
  steam: { icon: Steam, disableIcon: SteamDisable, color: '#3E617F' },
  playstation: {
    icon: Playstation,
    disableIcon: PlaystationDisable,
    color: '#0070D1',
  },
  nintendo: {
    icon: Nintendo,
    disableIcon: NintendoDisable,
    color: '#EE0003',
  },
  default: {
    icon: Default,
    disableIcon: DefaultDisable,
    cololist: SHOW_DIMENSION_DEFAULT_COLOR,
  },
  iphone: {
    icon: Iphone,
    disableIcon: IphoneDisable,
    cololist: SHOW_DIMENSION_DEFAULT_COLOR,
  },
  sentiment_positive: {
    icon: SENTIMENT_POSITIVE_ICON,
    disableIcon: SENTIMENT_POSITIVE_ICON,
    cololist: SHOW_DIMENSION_DEFAULT_COLOR,
  },
  sentiment_negative: {
    icon: SENTIMENT_NEGATIVE_ICON,
    disableIcon: SENTIMENT_NEGATIVE_ICON,
    cololist: SHOW_DIMENSION_DEFAULT_COLOR,
  },
  sentiment_neutral: {
    icon: SENTIMENT_NEUTRAL_ICON,
    disableIcon: SENTIMENT_NEUTRAL_ICON,
    cololist: SHOW_DIMENSION_DEFAULT_COLOR,
  },
  monitor: {
    icon: Monitor,
    disableIcon: MonitorDisable,
    cololist: SHOW_DIMENSION_DEFAULT_COLOR,
  },
  console_line: {
    icon: Console,
    disableIcon: ConsoleDisable,
    cololist: SHOW_DIMENSION_DEFAULT_COLOR,
  },
} as const satisfies Record<string, IDimensionTrendIconMeta>;
